function humanizeSegment(segment) {
  return segment
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Human-readable header trail for the current route (aligns with sidebar labels where possible).
 * @param {string} pathname
 * @returns {{ crumbs: { label: string, to?: string, help?: string }[], fullTitle: string } | null }
 */
export default function resolveHeaderLocation(pathname) {
  if (typeof pathname !== 'string' || !pathname) return null;
  const trimmed = pathname.replace(/\/+$/, '');
  const normalized = trimmed === '' ? '/' : trimmed;
  const segments = normalized.split('/').filter(Boolean);
  if (!segments.length) return null;

  const root = segments[0];

  if (root === 'dashboard') return resolveDashboard(segments);
  if (root === 'organization') return resolveOrganization(segments);
  if (root === 'admin') return resolveAdmin(segments);

  if (root === 'purchase' && segments.length === 1) {
    return withFullTitle([{ label: 'Admin', to: '/admin' }, { label: 'Buy Subscriptions' }]);
  }

  if (root === 'contact-us') {
    return withFullTitle([{ label: 'Contact Us' }]);
  }

  if (normalized === '/onboarding/registration') {
    return withFullTitle([{ label: 'Broker Registration' }]);
  }

  return null;
}

function withFullTitle(crumbs) {
  const fullTitle = crumbs.map((c) => c.label).join(' › ');
  return { crumbs, fullTitle };
}

/** @param {string[]} segments */
function resolveDashboard(segments) {
  const [, a, b] = segments;
  const base = { label: 'Dashboard', to: '/dashboard' };

  if (!a) return withFullTitle([base, { label: 'Overview' }]);

  const TOP = {
    plans: { label: 'Plans' },
    'business-units': { label: 'Business Units' },
    teams: { label: 'Teams' },
    policy: { label: 'Accountable Members' },
    'recovery-strategy': { label: 'Recovery Strategy' },
    'plan-document': { label: 'Business Continuity Plan' },
    'testing-exercising': { label: 'Testing & Exercising' },
    documents: { label: 'Documents' },
    'activation-levels': { label: 'Activation Levels' },
    glossary: { label: 'Glossary' },
  };

  if (TOP[a] && !b) return withFullTitle([base, TOP[a]]);

  if (a === 'testing-exercising') {
    const parent = { label: 'Testing & Exercising', to: '/dashboard/testing-exercising' };
    if (!b) return withFullTitle([base, parent]);
    return withFullTitle([base, parent, { label: 'Scenario Walkthrough' }]);
  }

  if (a === 'risk') {
    const riskParent = { label: 'Enterprise Risk', to: '/dashboard/risk/exposures' };
    const RISK_SCREENS = {
      exposures: 'Risk Exposures',
      assessment: 'Risk and Threat Assessment',
      register: 'Risk Register',
      report: 'Risk & Insurance Report',
    };
    const screen = b && RISK_SCREENS[b];
    if (screen) return withFullTitle([base, riskParent, { label: screen }]);
  }

  if (a === 'business-impact-analysis') {
    const biaParent = {
      label: 'Business Impact Analysis',
      to: '/dashboard/business-impact-analysis/strategic',
    };
    const BIA_SCREENS = {
      strategic: 'Strategic Business Impact Analysis',
      tactical: 'Tactical Business Impact Analysis',
      operational: 'Operational Business Impact Analysis',
    };
    const screen = b && BIA_SCREENS[b];
    if (screen) return withFullTitle([base, biaParent, { label: screen }]);
  }

  return withFullTitle([base, { label: humanizeSegment(a) }]);
}

/** @param {string[]} segments */
function resolveOrganization(segments) {
  const [, a] = segments;
  const base = { label: 'Organisation', to: '/organization' };

  if (!a) return withFullTitle([base, { label: 'Organisation Settings' }]);

  const MAP = {
    list: { label: 'Organisations' },
    contacts: { label: 'Internal Contacts' },
    customers: { label: 'Strategic Customers' },
    suppliers: { label: 'Strategic Suppliers' },
    'strategic-third-parties': { label: 'Strategic Third Parties' },
    'strategic-crisis-experts': { label: 'Strategic Crisis Experts' },
    'strategic-areas': { label: 'Strategic Areas' },
    policy: { label: 'Business Continuity Policy' },
    plans: { label: 'Plans' },
    users: { label: 'Users' },
  };

  if (MAP[a]) return withFullTitle([base, MAP[a]]);
  return withFullTitle([base, { label: humanizeSegment(a) }]);
}

/** @param {string[]} segments */
function resolveAdmin(segments) {
  const [, a] = segments;
  const base = { label: 'Admin', to: '/admin' };

  if (!a) return withFullTitle([{ label: 'Admin' }]);

  const MAP = {
    organisations: { label: 'Organisations & Plans' },
    'admin-users': { label: 'My Admin Users' },
    'my-subscriptions': { label: 'My Subscriptions' },
    'brokerage-subscriptions': {
      label: 'Subscription Management',
      help: [
        'This is the admin subscription page for managing current subscriptions and onboarding. ',
        'Individual subscriptions purchased separately are not included here.',
      ].join(''),
    },
    transactions: { label: 'Transaction History' },
    settings: { label: 'Settings' },
  };

  if (MAP[a]) return withFullTitle([base, MAP[a]]);
  return withFullTitle([base, { label: humanizeSegment(a) }]);
}
